import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

// One page for all the HSEQ forms that auto-upload to SharePoint.
//
// It shows problems by default - failures, retries, anything still queued -
// because that is the only thing anyone needs to act on. When every form has
// landed the page is empty, and that is the good outcome. "All" is there for
// the "did today's forms go?" question.

const STATES = {
  failed: { label: "Failed", bg: "#fef2f2", fg: "#b91c1c", icon: "fa-solid fa-triangle-exclamation" },
  retrying: { label: "Retrying", bg: "#fffbeb", fg: "#b45309", icon: "fa-solid fa-rotate" },
  uploading: { label: "Uploading", bg: "#eef2ff", fg: "#4338ca", icon: "fa-solid fa-cloud-arrow-up" },
  queued: { label: "Queued", bg: "#eff6ff", fg: "#1d4ed8", icon: "fa-solid fa-clock" },
  synced: { label: "Synced", bg: "#ecfdf5", fg: "#047857", icon: "fa-solid fa-circle-check" },
};

const COUNT_ORDER = ["failed", "retrying", "uploading", "queued", "synced"];

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  return apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function StateBadge({ state }) {
  const style = STATES[state] || STATES.queued;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: style.bg,
        color: style.fg,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <i className={style.icon} aria-hidden="true" />
      {style.label}
    </span>
  );
}

export default function SharePointSyncPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({});
  const [forms, setForms] = useState([]);
  const [view, setView] = useState("problems");
  const [formSlug, setFormSlug] = useState("");
  const [retrying, setRetrying] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ view });
      if (formSlug) params.set("formSlug", formSlug);
      const res = await fetch(`${getBaseUrl()}/api/sharepoint/sync/jobs?${params}`, {
        headers: authHeaders(),
      });
      const out = await res.json().catch(() => null);
      if (!res.ok) throw new Error(out?.message || "Could not load the sync queue");
      setJobs(Array.isArray(out?.data?.jobs) ? out.data.jobs : []);
      setCounts(out?.data?.counts || {});
      setForms(Array.isArray(out?.data?.autoSyncedForms) ? out.data.autoSyncedForms : []);
    } catch (e) {
      setError(e?.message || "Could not load the sync queue");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [view, formSlug]);

  useEffect(() => {
    load();
  }, [load]);

  // Uploads finish in seconds, so a slow refresh keeps the page honest without
  // hammering the API while someone reads it.
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const retry = async (job) => {
    const key = `${job.formSlug}|${job.recordId}`;
    if (retrying.has(key)) return;
    setRetrying((prev) => new Set(prev).add(key));
    try {
      const res = await fetch(`${getBaseUrl()}/api/sharepoint/sync/retry`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ formSlug: job.formSlug, recordId: job.recordId }),
      });
      const out = await res.json().catch(() => null);
      if (!res.ok) throw new Error(out?.message || "Could not queue the retry");
      toast.success(out?.message || "Queued for upload to SharePoint.");
      await load();
    } catch (e) {
      toast.error(e?.message || "Could not queue the retry");
    } finally {
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const formNameBySlug = useMemo(() => {
    const map = {};
    for (const f of forms) map[f.slug] = f.formName;
    return map;
  }, [forms]);

  const problemCount = COUNT_ORDER.filter((s) => s !== "synced").reduce(
    (total, s) => total + (counts[s] || 0),
    0
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700 }}>SharePoint Sync</h4>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
            HSEQ forms upload to SharePoint on their own the moment they are submitted. Anything that did
            not land shows up here.
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            border: "1px solid #cbd5e1",
            background: "#fff",
            borderRadius: 8,
            padding: "8px 14px",
            fontSize: 13,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          <i className={loading ? "fa fa-spinner fa-spin" : "fa-solid fa-rotate-right"} style={{ marginRight: 6 }} />
          Refresh
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "18px 0" }}>
        {COUNT_ORDER.map((state) => (
          <div
            key={state}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "10px 16px",
              minWidth: 110,
              background: "#fff",
            }}
          >
            <div style={{ fontSize: 12, color: STATES[state].fg, fontWeight: 600 }}>{STATES[state].label}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{counts[state] || 0}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "inline-flex", border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
          {[
            { key: "problems", label: `Needs attention${problemCount ? ` (${problemCount})` : ""}` },
            { key: "all", label: "All" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setView(tab.key)}
              style={{
                border: "none",
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                background: view === tab.key ? "#8b1634" : "#fff",
                color: view === tab.key ? "#fff" : "#334155",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <select
          value={formSlug}
          onChange={(e) => setFormSlug(e.target.value)}
          style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 13 }}
        >
          <option value="">All forms</option>
          {forms.map((f) => (
            <option key={f.slug} value={f.slug}>
              {f.formName}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", color: "#b91c1c", padding: "10px 14px", borderRadius: 8, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflowX: "auto", background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              <th style={{ padding: "10px 14px" }}>Form</th>
              <th style={{ padding: "10px 14px" }}>Status</th>
              <th style={{ padding: "10px 14px" }}>File</th>
              <th style={{ padding: "10px 14px" }}>Tries</th>
              <th style={{ padding: "10px 14px" }}>Last attempt</th>
              <th style={{ padding: "10px 14px" }}>Problem</th>
              <th style={{ padding: "10px 14px" }} />
            </tr>
          </thead>
          <tbody>
            {loading && jobs.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 28, textAlign: "center", color: "#64748b" }}>
                  Loading…
                </td>
              </tr>
            )}

            {!loading && jobs.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 34, textAlign: "center", color: "#64748b" }}>
                  {view === "problems" ? (
                    <>
                      <i className="fa-solid fa-circle-check" style={{ color: "#047857", marginRight: 8 }} />
                      Everything has reached SharePoint.
                    </>
                  ) : (
                    "Nothing has been queued yet."
                  )}
                </td>
              </tr>
            )}

            {jobs.map((job) => {
              const key = `${job.formSlug}|${job.recordId}`;
              const busy = retrying.has(key);
              return (
                <tr key={job._id || key} style={{ borderTop: "1px solid #eef2f7" }}>
                  <td style={{ padding: "10px 14px" }}>
                    {job.formName || formNameBySlug[job.formSlug] || job.formSlug}
                    <div style={{ color: "#94a3b8", fontSize: 11 }}>{job.recordId}</div>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <StateBadge state={job.state} />
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {job.sharepointWebUrl ? (
                      <a href={job.sharepointWebUrl} target="_blank" rel="noopener noreferrer">
                        {job.sharepointFileName || "Open in SharePoint"}
                      </a>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>{job.sharepointFileName || "—"}</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px" }}>{job.attempts || 0}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{formatDateTime(job.lastAttemptAt)}</td>
                  <td style={{ padding: "10px 14px", color: "#b91c1c", maxWidth: 340 }}>{job.lastError || "—"}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>
                    {job.state !== "synced" && (
                      <button
                        type="button"
                        onClick={() => retry(job)}
                        disabled={busy}
                        style={{
                          border: "1px solid #cbd5e1",
                          background: "#fff",
                          borderRadius: 8,
                          padding: "6px 12px",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: busy ? "not-allowed" : "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <i
                          className={busy ? "fa fa-spinner fa-spin" : "fa-solid fa-rotate-right"}
                          style={{ marginRight: 6 }}
                        />
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
